import React, { type ReactNode } from "react";

import { Navigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import Loader from "../Loader/Loader";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, role } = useAuth();

  // Wait for auth check to complete
  if (loading) {
    // Use a non-blocking loading state that doesn't prevent sidebar navigation
    // Position fixed to cover only the main content area, not the sidebar
    return <Loader classname="default-loader" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If roles are specified, enforce RBAC.
  // An empty array denies: it means the pageId resolved to no roles at all
  // (unregistered page), which must fail closed. Only `undefined` — the caller
  // opting out of role gating entirely — passes through ungated.
  if (allowedRoles) {
    const isAllowed = allowedRoles.includes(role);
    if (!isAllowed) {
      // This should rarely happen now since role validation is done during login
      // But we'll keep this as a fallback and show an explicit forbidden page
      return <Navigate to="/error?status=403" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
